import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { Filters } from '../../interfaces/filters';
import { Lane, LanesResponse } from '../../interfaces/services/lanes';
import { Order, OrderResponse } from '../../interfaces/services/orders';
import { ApiStatus, EquipmentTypeCode } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';

interface LaneDetailState {
    laneDetailsApiStatus: ApiStatus;
    laneDetails: Lane;
    inboundOrdersApiStatus: ApiStatus;
    inboundOrders: Order[];
    inboundOrdersCount: number;
    outboundOrdersApiStatus: ApiStatus;
    outboundOrders: Order[];
    outboundOrdersCount: number;
}

/** Thunks */
export const fetchLaneDetails = createAsyncThunk(
    'lane/fetchLaneDetails',
    async (
        {
            pickup_service_area_id,
            delivery_service_area_id,
            filter_criteria,
        }: {
            pickup_service_area_id: string | null;
            delivery_service_area_id: string | null;
            filter_criteria: Filters;
        },
        thunkApi
    ) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/lanes`,
                body: {
                    pickup_service_area_id,
                    delivery_service_area_id,
                    filter_criteria,
                },
            }) as AxiosResponse<LanesResponse>;

            if (data.lanes.length === 0) {
                return thunkApi.rejectWithValue(data);
            }

            return data;
        } catch (error) {
            console.error('Unable to load lane details', error);
            toast.error('Unable to load lane details');
            throw error;
        }
    }
);

export const fetchInboundOrders = createAsyncThunk(
    'lane/fetchInboundOrders',
    async ({
        pickup_service_area_id,
        delivery_service_area_id,
        mri_freight_order_specified_eqpt_code,
        filter_criteria,
    }: {
        pickup_service_area_id?: string;
        delivery_service_area_id?: string;
        mri_freight_order_specified_eqpt_code: EquipmentTypeCode;
        filter_criteria: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/orders`,
                body: {
                    ...pickup_service_area_id && {
                        pickup_service_area_id,
                    },
                    ...delivery_service_area_id && {
                        delivery_service_area_id,
                    },
                    filter_criteria: {
                        ...filter_criteria,
                        mri_freight_order_specified_eqpt_code,
                    },
                },
            }) as AxiosResponse<OrderResponse>;

            return data;
        } catch (error) {
            console.error(`Unable to load orders from ${pickup_service_area_id} to ${delivery_service_area_id}`, error);
            toast.error(`Unable to load orders from ${pickup_service_area_id} to ${delivery_service_area_id}`);
            throw error;
        }
    }
);

export const fetchOutboundOrders = createAsyncThunk(
    'lane/fetchOutboundOrders',
    async ({
        pickup_service_area_id,
        delivery_service_area_id,
        filter_criteria,
    }: {
        pickup_service_area_id?: string;
        delivery_service_area_id?: string;
        filter_criteria: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/orders`,
                body: {
                    ...pickup_service_area_id && {
                        pickup_service_area_id,
                    },
                    ...delivery_service_area_id && {
                        delivery_service_area_id,
                    },
                    filter_criteria,
                },
            }) as AxiosResponse<OrderResponse>;

            return data;
        } catch (error) {
            console.error(`Unable to load orders from ${delivery_service_area_id} to ${pickup_service_area_id}`, error);
            toast.error(`Unable to load orders from ${delivery_service_area_id} to ${pickup_service_area_id}`);
            throw error;
        }
    }
);

const initialState: LaneDetailState = {
    laneDetailsApiStatus: ApiStatus.Idle,
    laneDetails: {
        pickup_service_area_id: '',
        pickup_service_area_name: null,
        pickup_service_area_latitude_value: null,
        pickup_service_area_longitude_value: null,
        delivery_service_area_id: '',
        delivery_service_area_name: null,
        delivery_service_area_latitude_value: null,
        delivery_service_area_longitude_value: null,
        freight_order_count: null,
        total_revenue_usd_amount: null,
        linehaul_revenue_usd_amount: null,
        fuel_surcharge_usd_amount: null,
        accessorial_revenue_usd_amount: null,
        travel_distance_miles_amount: null,
        billable_distance_miles_amount: null,
        loaded_distance_miles_amount: null,
        empty_distance_miles_amount: null,
        pta_to_last_stop_departure_hours_duration: null,
        dispatch_to_last_stop_departure_hours_duration: null,
        average_dispatch_to_last_stop_departure_hours_duration: null,
        first_stop_to_last_stop_hours_duration: null,
        average_first_stop_to_last_stop_hours_duration: null,
        first_stop_departure_to_final_stop_arrival_hours_duration: null,
        average_first_stop_departure_to_final_stop_arrival_hours_duration: null,
        standard_transit_hours_duration: null,
        netwise_dwell_hours_duration: null,
        netwise_in_transit_delay_hours_duration: null,
        netwise_delay_hours_duration: null,
        load_hours_duration: null,
        unload_hours_duration: null,
        empty_transit_hours_duration: null,
        pickup_party_count: null,
        average_scheduled_miles_per_hour: null,
        average_actual_miles_per_hour: null,
        average_scheduled_vs_actual_miles_per_hour: null,
        average_scheduled_vs_actual_miles_per_hour_rank: null,
        average_fuel_surcharge_usd_per_mile_amount: null,
        average_loaded_distance_miles_amount: null,
        linehaul_rate_usd_amount: null,
        freight_order_bad_data_order_count: null,
        average_standard_transit_hours_duration: null,
        average_netwise_in_transit_delay_hours_duration: null,
        average_load_hours_duration: null,
        average_unload_hours_duration: null,
        average_empty_transit_hours_duration: null,
        lane_yield_usd: null,
    },
    inboundOrdersApiStatus: ApiStatus.Idle,
    inboundOrders: [],
    inboundOrdersCount: 0,
    outboundOrdersApiStatus: ApiStatus.Idle,
    outboundOrders: [],
    outboundOrdersCount: 0,
};

export const laneDetailsSlice = createSlice({
    name: 'lane',
    initialState,
    reducers: {
        clearLaneDetailsData: (): LaneDetailState => {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchLaneDetails.pending, (state) => {
            state.laneDetailsApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchLaneDetails.fulfilled, (state, action) => {
            state.laneDetailsApiStatus = ApiStatus.Success;
            [state.laneDetails] = action.payload.lanes;
        });
        builder.addCase(fetchLaneDetails.rejected, (state) => {
            state.laneDetailsApiStatus = ApiStatus.Failure;
            state.laneDetails = initialState.laneDetails;
        });

        builder.addCase(fetchInboundOrders.pending, (state) => {
            state.inboundOrdersApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchInboundOrders.fulfilled, (state, action) => {
            state.inboundOrdersApiStatus = ApiStatus.Success;
            state.inboundOrders = action.payload.freight_orders;
            state.inboundOrdersCount = action.payload.freight_order_count;

            if (action.payload.freight_orders.length === 0) {
                toast.info("We didn't find any orders on this lane that match your search criteria. Try adjusting your filters");
            }
        });
        builder.addCase(fetchInboundOrders.rejected, (state) => {
            state.inboundOrdersApiStatus = ApiStatus.Failure;
            state.inboundOrders = [];
            state.inboundOrdersCount = 0;
        });

        builder.addCase(fetchOutboundOrders.pending, (state) => {
            state.outboundOrdersApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchOutboundOrders.fulfilled, (state, action) => {
            state.outboundOrdersApiStatus = ApiStatus.Success;
            state.outboundOrders = action.payload.freight_orders;
            state.outboundOrdersCount = action.payload.freight_order_count;
        });
        builder.addCase(fetchOutboundOrders.rejected, (state) => {
            state.outboundOrdersApiStatus = ApiStatus.Failure;
            state.outboundOrders = [];
            state.outboundOrdersCount = 0;
        });
    },
});

export default laneDetailsSlice.reducer;

export const {
    clearLaneDetailsData,
} = laneDetailsSlice.actions;
